import React, { useState, useEffect, useRef } from 'react';
import useFetch from '../hooks/useFetch';
import useForm from '../hooks/useForm';
import styled from 'styled-components';
// import DataChart from './DataChart';
import StackingDataChart from './StackingDataChart';
// import ExpandableCategoriesSection from './ExpandableCategoriesSection';
import { LoadingIcon } from '@prosolve/common-components-ui'
import Filters from './Filters';
import { objectToQueryString } from '../dataHooks/dataHookUtils'
import useDataUserAccount from '../dataHooks/useDataUserAccount'
import useQuestDashboardAuthMessage from '../hooks/useQuestDashboardAuthMessage'
import Cookies from 'js-cookie';
import Tabs from '../sharedComponents/Tabs';
import ClassListTable from './ClassListTable';

const SectionRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 20px;
  padding-bottom: 20px;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  border: 1px solid #dedede;
  border-radius: 5px;
  background: #fdfdfd;
  padding: 10px;
  flex: 1;
  /* max-width: calc(50% - 10px); */
  overflow: hidden;
`;

export const SectionTitle = styled.div`
  color: #565656;
  font-weight: bold;
  font-size: 22px;
  margin-bottom: 10px;
`;

export const SectionLoading = () => (
  <div style={{
    // flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px' // Use 400 since this is the internal default height of a highchart
  }}>
    <LoadingIcon />
  </div>
)

const SectionChart = ({
  isLoading,
  title,
  analyticsData,
  dataPoints,
  categories
}) => {
  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      {
        isLoading ? <SectionLoading /> :
          <StackingDataChart
            analyticsData={analyticsData}
            dataPoints={dataPoints}
            categories={categories}
          />
      }
    </Section>
  )
}

export default () => {
  const [isLoading, setIsLoading] = useState(true)
  const [analyticsData, setAnalyticsData] = useState();

  const [singleAccountOnly, setSingleAccountOnly] = useState(false)

  const [restrictedToOwnedClasses, setRestrictedToOwnedClasses] = useState()

  const [verifyingAccess, setVerifyingAccess] = useState(true)
  const [revokeAccess, setRevokeAccess] = useState(false)

  const currentFetchValuesRef = useRef()

  const filtersFormSchema = {
    requiredFields: [],
    optionalFields: ['accountId', 'implementationId', 'classId', 'teamColor'],
    initData: {}
  };
  
  const formData = useForm(filtersFormSchema)

  const userId = Cookies.get('loggedInUserId') || sessionStorage.getItem('loggedInUserId');

  // sessionStorage on this origin outlives the parent portal's login session, so a
  // re-login as a different user would otherwise keep showing the previous user's view.
  // The parent re-sends the auth token on every iframe load; if it names a different
  // user than the one cached here, re-sync and reload.
  useQuestDashboardAuthMessage((messageUserId) => {
    if (messageUserId !== sessionStorage.getItem('loggedInUserId')) {
      sessionStorage.setItem('isQuestAssessmentDashboardAuthorized', 'true');
      sessionStorage.setItem('loggedInUserId', messageUserId);
      window.location.reload();
    }
  });

  const { fetchUserAccount } = useDataUserAccount()
  useEffect(() => {
    (async () => {
      setVerifyingAccess(true)
      try {
        const userData = await fetchUserAccount(userId || '') || {}
        const { 
          accountId = '', 
          isSuperUser,
          hasAccountAccess
          // userType,
          // licenseAccess
        } = userData;
        if (!accountId) {
          setRevokeAccess(true)
        } else if (!isSuperUser) {
          formData.setValues({ ...formData.values, accountId })
          setSingleAccountOnly(true)
          // Check even further here if the non super user is a teacher and only restricted to their classes
          // const restrictedToClasses = (userType !== 'Client' && licenseAccess !== true)
          setRestrictedToOwnedClasses(!hasAccountAccess)
        }
      } catch(e) {
        console.log(e)
        setRevokeAccess(true)
      }
      setVerifyingAccess(false)
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetch = useFetch();

  const timeoutRef = useRef();
  const fetchAnalyticsDataDeferred = async () => {
    setIsLoading(true);
    currentFetchValuesRef.current = formData.values;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      fetchAnalyticsData(formData.values)
    }, 500)
  }

  const fetchAnalyticsData = async (formDataValues) => {
    try {
      const { accountId, implementationId, classId, teamColor } = formDataValues;
      const url = `assessment/analytics/questDashboard${objectToQueryString({ accountId, implementationId, classId, teamColor })}`;
      const response = await fetch(url, { method: 'GET' })
      const jsonData = await response.json();
      if (jsonData.error) {
        setAnalyticsData(undefined)
      } else {
        setAnalyticsData(jsonData)
      }
      setIsLoading(false);
    } catch (e) {
      console.log(e)
      setAnalyticsData(undefined)
      setIsLoading(false);
    }
  }

  // Need to do this becausee search common comps are triggering a change event on formData.values so need this to prevent double API calls
  const preventDoubleOnLoadCall = (values) => {
    let allow = false;
    if (!currentFetchValuesRef.current) {
      return true;
    } else {
      const { accountId, implementationId, classId, teamColor } = values;
      const { accountId: aId, implementationId: iId, classId: cId, teamColor: tId } = currentFetchValuesRef.current;

      if (accountId !== aId) {
        allow = true;
      } else if (implementationId !== iId) {
        allow = true;
      } else if (classId !== cId) {
        allow = true;
      } else if (teamColor !== tId) {
        allow = true;
      }

      return allow;
    }
  }

  useEffect(() => {
    if (!verifyingAccess && preventDoubleOnLoadCall(formData.values)) {
      if (restrictedToOwnedClasses) {
        // If we are in restrictedToOwnedClasses we only ever fetch when classId is not set to an empty string ''
        if (formData.values.classId) {
          fetchAnalyticsDataDeferred()
        }
      } else {
        fetchAnalyticsDataDeferred()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.values, verifyingAccess])


  const tabs = ['Human Skills', 'SEL Skills', 'Conditions & Climate', 'Academic Engagement', 'Class List']
  
  const [selectedTab, setSelectedTab] = useState('Human Skills')

  const teacherDataPoints = [
    { assessmentName: 'MergedTeacherSDPre2025', label: 'PreSurvey' },
    { assessmentName: 'MergedTeacherSDPost2025', label: 'PostSurvey' }
  ]

  const studentDataPoints = [
    { assessmentName: 'MergedStudentSDPre2025', label: 'PreSurvey' },
    { assessmentName: 'MergedStudentSDPost2025', label: 'PostSurvey' }
  ]

  const humanSkillCategories = [
    'Communication', 
    'Collaboration',
    'Critical Thinking',
    'Creative Problem Solving',
    'Curiosity',
    'Adaptability',
    'Character',
  ]

  const selSkillCategories = [
    'Responsible Decision Making',
    'Social Awareness',
    'Relationship Skills',
    'Self-Management',
    'Self Awareness',
  ]

  const conditionAndClimateSkillCategories = [
    'Connectedness',
    'Climate for Adaptability & Resilience',
    'Positive Relationships',
  ]

  const academicEngagementSkillCategories = [
    'Motivation & Perseverance',
    'Agency & Ownership',
    'Positive Experience & Relevance',
  ]

  return (verifyingAccess || revokeAccess)
    ? 
    <div style={{
      flex: 1,
      display: 'flex',
      height: '100%',
      color: '#565656',
      fontWeight: 'bold',
      fontSize: '32px',
      textAlign: 'center',
      overflow: 'auto',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {
        verifyingAccess ?
        <LoadingIcon />
        :
        <div>
          {'Unable to validate permissions to access this page'}
        </div>
      }
    </div> :(
    <div style={{
      height: '100%',
      width: '100%',
      background: '#f5f5f5',
      display: 'flex',
      overflow: 'hidden auto'
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px'
      }}>
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div 
            style={{
              color: '#565656',
              fontSize: '24px',
              fontWeight: 'bold',
              // marginBottom: '20px'
            }}
          >
            {'Assessment Dashboard'}
          </div>
          <Filters formData={formData} singleAccountOnly={singleAccountOnly} restrictedToOwnedClasses={restrictedToOwnedClasses} />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <Tabs tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        </div>
        <SectionRow>
          { selectedTab === 'Human Skills' &&
            <>
            {/* <ExpandableCategoriesSection
              title='Student Episode Human Skills'
              isLoading={isLoading}
              analyticsData={analyticsData}
              categories={humanSkillCategories}
            /> */}
            <SectionChart
              title='Student Human Skills'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={studentDataPoints}
              categories={humanSkillCategories}
            />
            </>
          }
          { selectedTab === 'SEL Skills' &&
            <>
            {/* <ExpandableCategoriesSection
              title='Student Episode SEL Skills'
              isLoading={isLoading}
              analyticsData={analyticsData}
              categories={selSkillCategories}
            /> */}
            <SectionChart
              title='Student SEL Skills'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={studentDataPoints}
              categories={selSkillCategories}
            />
            </>
          }
          { selectedTab === 'Conditions & Climate' &&
            <>
            {/* <ExpandableCategoriesSection
              title='Student Episode Conditions & Climate'
              isLoading={isLoading}
              analyticsData={analyticsData}
              categories={conditionAndClimateSkillCategories}
            /> */}
            <SectionChart
              title='Student Conditions & Climate'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={studentDataPoints}
              categories={conditionAndClimateSkillCategories}
            />
            </>
          }
          { selectedTab === 'Academic Engagement' &&
            <>
            {/* <ExpandableCategoriesSection
              title='Student Episode Academic Engagement'
              isLoading={isLoading}
              analyticsData={analyticsData}
              categories={academicEngagementSkillCategories}
            /> */}
            <SectionChart
              title='Student Academic Engagement'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={studentDataPoints}
              categories={academicEngagementSkillCategories}
            />
            </>
          }
        </SectionRow>
        <SectionRow>
          { selectedTab === 'Human Skills' &&
            <SectionChart
              title='Teacher Human Skill Perceptions'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={teacherDataPoints}
              categories={humanSkillCategories}
            />
          }
          { selectedTab === 'SEL Skills' &&
            <SectionChart
              title='Teacher SEL Skill Perceptions'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={teacherDataPoints}
              categories={selSkillCategories}
            />
          }
          { selectedTab === 'Conditions & Climate' &&
            <SectionChart
              title='Teacher Condition & Climate Perceptions'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={teacherDataPoints}
              categories={conditionAndClimateSkillCategories}
            />
          }
          { selectedTab === 'Academic Engagement' &&
            <SectionChart
              title='Teacher Academic Engagement Perceptions'
              isLoading={isLoading}
              analyticsData={analyticsData}
              dataPoints={teacherDataPoints}
              categories={academicEngagementSkillCategories}
            />
          }
          { selectedTab === 'Class List' &&
            <ClassListTable
              filterValues={formData.values}
              isLoading={isLoading}
              analyticsData={analyticsData}
            />
          }
        </SectionRow>
      </div>
    </div>
  )
}